<template>
  <div>
    <nav-bar />
    <v-container class="mt-10 pt-5">
      <!-- Page Header ########################################### -->
      <header-page titleText="Gestão de Conselhos" titleIcon="fal fa-atlas" />

      <v-container pa-6>
        <v-row justify="space-between">
          <v-col cols="auto">
            <p class="list-labels">Lista de Conselhos</p>
          </v-col>
          <v-skeleton-loader
            ref="skeleton"
            :boilerplate="false"
            type="table-tfoot"
            :tile="false"
            class="ml-auto"
            v-show="loading"
            min-width="300px"
          ></v-skeleton-loader>
          <v-col cols="auto" v-show="!loading">
            <v-container grid-list-md pa-0>
              <v-layout justify-start wrap justify-sm-end>
                <v-flex col-12 col-sm-6>
                   <v-combobox
                    v-model="currentSearchTerm"
                    :loading="loading"
                    :items="items"
                    label="Buscar"
                    outlined
                    clearable
                    dense
                    @change="addSearchTerm($event, true)"        
                  ></v-combobox>
                </v-flex>
                <v-flex flex-grow-0 col-12 col-sm-4>
                  <v-select
                    v-model="selectedOrderBy"
                    :items="orderByOptions"
                    label="Ordenar por"
                    item-text="name"
                    item-value="id"
                    outlined
                    hide-details="auto"
                    dense
                    solo
                    flat
                    @change="loadCouncils"
                  ></v-select>
                </v-flex>
              </v-layout>
            </v-container>
          </v-col>
        </v-row>
      </v-container>

      <!-- Search Results ########################################### -->
      <search-result
        inset
        :totalResults="totalNumberOfCouncils"
        v-model="searchTerms"
        @itemRemoved="searchTermRemoved()"
        :addButtonDestiny="routes.app.CouncilRegisterNew"
      />

      <!-- Page Body ########################################### -->
      <v-container>
        <template >
          <v-row
          v-for="council in councils" :key="council.id"
            class="main-list-item mx-0 align-center justify-between"
          >
            <!-- data list #########################-->
            <v-col
              cols="12"
              sm="8"
              md="6"
              lg="auto"
              class="pb-0 flex-grow-1 flex-shrink-0 pt-sm-0 pt-12"
            >
              <div>
                <span class="tiny-label">{{
                  council.entityViewModel.socialReason
                }}</span>
              </div>
              <h4 class="font-weight-regular">
                {{ council.entityViewModel.name }}
                <span class="caption op-5">{{
                  council.entityViewModel.document | FormatDocument
                }}</span>
              </h4>
              <div class="op-5">
                <em>{{ incentiveLawName(council.incentiveLawId) }}</em>
              </div>
            </v-col>
            <v-spacer></v-spacer>

            <!-- tools #########################-->
            <div class="tools">
              <v-container fluid pa-0>
                <v-row no-wrap align="center" no-gutters>
                  <!-- creation date #########################-->
                  <v-col cols="auto">
                    <v-layout class="pr-3" align-end column>
                      <v-tooltip top>
                        <template v-slot:activator="{ on }">
                          <v-flex>
                            <v-icon
                              color="var(--text)"
                              class="mr-2"
                              size="16"
                              v-on="on"
                              >fal fa-user-tag</v-icon
                            >
                            <span class="tiny-label">{{
                              getCouncilAge(council.entityViewModel)
                            }}</span>
                          </v-flex>
                        </template>
                        <span
                          >Cadastrado em:
                          {{
                            council.entityViewModel.creationDate | FormatDate
                          }}</span
                        >
                      </v-tooltip>
                    </v-layout>
                  </v-col>

                  <v-col
                    v-if="isAdminOrBenefactor"
                    cols="auto"
                    class="flex-grow-0 no-wrap ml-3 d-none d-md-block"
                  >
                    <!-- council skin & configs #########################-->
                    <v-layout>
                      <div>
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-btn                              
                              icon
                              fab
                              small
                              color="var(--gray)"
                              class="hover-red"
                              v-on="on"
                              @click="gotoCouncilConfig(council)"
                            >
                              <v-icon size="20">fal fa-cog</v-icon>
                            </v-btn>
                          </template>
                          <span>Configurações do site</span>
                        </v-tooltip>
                      </div>
                    </v-layout>
                  </v-col>

                  <!-- context menu - mobile #########################-->
                  <v-menu bottom left>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        v-on="on"
                        color="var(--gray)"
                        class="ml-3 d-flex d-md-none"
                      >
                        <v-icon>far fa-ellipsis-v</v-icon>
                      </v-btn>
                    </template>

                    <v-list dense>
                      <v-list-item v-for="(item, i) in contextMenu" :key="i">
                        <v-list-item-icon>
                          <v-icon size="16" v-text="item.icon"></v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>{{ item.menu }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>

                  <!-- edit & on/off #########################-->
                  <v-col 
                    cols="auto" 
                    v-if="isAdminOrBenefactor"
                    >
                    <v-layout align-center class="d-none d-md-flex flex-grow-0">
                      <v-flex pl-6>
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-btn
                              icon
                              fab
                              small
                              color="var(--gray)"
                              class="hover-red"
                              v-on="on"
                              @click="editCouncil(council)"
                            >
                              <v-icon size="20">fal fa-pen</v-icon>
                            </v-btn>
                          </template>
                          <span>Editar</span>
                        </v-tooltip>
                      </v-flex>
                      <v-flex pl-6>
                        <v-tooltip top>
                          <template v-slot:activator="{ on }">
                            <v-btn
                              icon
                              fab
                              small
                              :color="council.published?'var(--green)':'var(--red)'"
                              class="hover-red"
                              v-on="on"
                              @click="changeCouncilPublishStatus(council)"
                            >
                              <v-icon size="20" v-show="council.published" color="green">fal fa-globe</v-icon>
                              <v-icon size="20" v-show="!council.published" color="red">fal fa-globe</v-icon>
                            </v-btn>
                          </template>
                          <span v-show="council.published">Cancelar Publicação</span>
                          <span v-show="!council.published">Publicar</span>
                        </v-tooltip>
                      </v-flex>
                      <v-flex pl-6>
                        <v-switch
                          v-model="council.entityViewModel.active"
                          @change="
                            updateCouncilStatus(council.entityViewModel.id)
                          "
                          inset
                        >
                        </v-switch>
                      </v-flex>
                    </v-layout>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </v-row>
        </template>
      </v-container>
    </v-container>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import NavBar from "@/components/NavBar";
import HeaderPage from "@/components/HeaderPage";
import SearchResult from "@/components/SearchResult";
import Routes from "@/router/names.js";
import CommonHelper from "@/scripts/helpers/common.helper";
import CouncilOrderByOptions from "@/scripts/enums/councilOrderByOptions.enum";
import EntityService from "@/scripts/services/entity.service";
import GeneralService from "@/scripts/services/general.service";

export default {
  name: "CouncilManagement",
  components: {
    NavBar,
    HeaderPage,
    SearchResult
  },
  data: () => ({
    routes: Routes,
    entityService: new EntityService(),
    generalService: new GeneralService(),
    incentiveLawList: [],
    loadingCount: false,
    loadingList: false,
    councils: [],
    items: [],
    totalNumberOfCouncils: 0,
    selectedCouncil: null,
    page: 1,
    dialog: false,
    searchTerms: [],
    searchWaitTime: 2000,
    selectedOrderBy: CouncilOrderByOptions.Name,
    currentSearchTerm: "",
    orderByOptions: [
      { id: CouncilOrderByOptions.CreationDate, name: "Ordenar por data" },
      { id: CouncilOrderByOptions.Name, name: "Ordenar por nome" },
      { id: CouncilOrderByOptions.Status, name: "Ordenar por status" }
    ],
    contextMenu: [
      { icon: "fal fa-pen", menu: "Editar" },
      { icon: "fal fa-ban", menu: "Desativar" }
    ]
  }),
  mounted() {
    this.loadCouncils();

    this.generalService
      .listAllIncentiveLaws()
      .then(result => (this.incentiveLawList = result));
  },
  computed: {
    ...mapGetters("userModule", ["loggedEntityId", "isAdminOrBenefactor"]),
    loading() {
      return this.loadingList || this.loadingCount;
    }
  },
  methods: {
    ...mapActions(["setSelectedCouncil"]),
    changeCouncilPublishStatus(council) {
      if(council.published) {
        this.entityService
        .unpublishCouncil(council.id)
        .then(() => {
          council.published = false;
        });
      }
      else {
        this.entityService
        .publishCouncil(council.id)
        .then(() => {
          council.published = true;
        });
      }
    },
    incentiveLawName(id) {
      if (this.incentiveLawList) {
        const incentiveLaw = this.incentiveLawList.find(law => law.id === id);
        if (incentiveLaw) return incentiveLaw.name;
      }
      return "";
    },
    getFilters() {
      return {
        orderBy: this.selectedOrderBy,
        currentPage: this.currentPage - 1,
        pageSize: this.selectedPagination,
        searchTerms: this.searchTerms
      };
    },
    loadCouncils() {
      if (this.loading) return;
      this.loadingCount = true;
      this.loadingList = true;
      this.searchTimeout = null;
      this.entityService
        .countCouncils(this.getFilters())
        .then(this.countCouncilsCallBack)
        .finally(() => (this.loadingCount = false));
      this.entityService
        .listCouncils(this.getFilters())
        .then(this.listCouncilsCallBack)
        .finally(() => (this.loadingList = false));
    },
    listCouncilsCallBack(data) {
      this.councils = data;
      if(this.items.length == 0) {
        this.councils.forEach(element => {
          this.items.push(element.entityViewModel.name);
        });
        this.item = this.items.sort();
      }
      this.loadingList = false;
    },
    countCouncilsCallBack(data) {
      this.totalNumberOfCouncils = data;
      this.loadingCount = false;
    },
    gotoCouncilConfig(council) {
      this.setSelectedCouncil(council);
      this.$router.push({
        name: Routes.app.CouncilConfig,
        params: { councilId: council.id }
      });
    },
    editCouncil(council) {
      this.setSelectedCouncil(council);
      this.$router.push({
        name: Routes.app.CouncilRegister,
        params: { councilId: council.id }
      });
    },
    updateCouncilStatus(entityId) {
      this.entityService
        .updateEntityStatus(entityId);
    },
    searchTermRemoved() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(this.loadCouncils, this.searchWaitTime);
    },
    addSearchTerm(event, addAnyway) {
     if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(this.loadCouncils, this.searchWaitTime);
      }
      if (event.key == "Enter" || addAnyway) {
        if (
          this.currentSearchTerm &&
          this.currentSearchTerm.length > 0 &&
          this.searchTerms.indexOf(this.currentSearchTerm) == -1
        )
          this.searchTerms.push(this.currentSearchTerm);
        this.currentSearchTerm = [];
        if (!this.searchTimeout)
          this.searchTimeout = setTimeout(this.loadCouncils, this.searchWaitTime);
      }
    },
    getCouncilAge(entity) {
      return CommonHelper.GetEntityAge(entity);
    }
  }
};
</script>